import { useEffect, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Delete, ExternalLink } from '@factorialco/f0-react/icons/app'
import {
  ReviewEmployeesModalDocument,
  ReviewEmployeesModalEmployeeFragment,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

interface Props {
  employeeIds: number[]
  isOpen: boolean
  onRemoveEmployee: (employeeId: number) => void
  onClose: () => void
}

const ReviewEmployeesModal = ({ employeeIds, isOpen, onRemoveEmployee, onClose }: Props) => {
  const { navigation } = useNavigation()
  const [ids, setIds] = useState(employeeIds)

  useEffect(() => {
    setIds(employeeIds)
  }, [employeeIds])

  const source = useGraphqlDataCollectionSource(
    {
      document: ReviewEmployeesModalDocument,
      fetchPolicy: 'network-only',
      mapVariables: () => ({
        employeeIds: ids,
        sort: {
          field: 'firstName',
          order: 'asc',
        },
      }),
      mapPagination: (data) => {
        const connection = data?.employees?.employeesConnection
        return {
          total: connection?.totalCount ?? 0,
          cursor: connection?.pageInfo?.endCursor ?? undefined,
          hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
        }
      },
      mapData: (data) => {
        return data?.employees?.employeesConnection?.nodes ?? []
      },
      totalItemSummary: (totalItems: number) =>
        i18n.t('contract_versions.additional_compensation.bulk_create.modal.employees_counter', {
          totalItems,
        }),
      paginationType: 'infinite-scroll',
      itemActions: (employee: ReviewEmployeesModalEmployeeFragment) => {
        const agreementAction = {
          label: i18n.t(
            'contract_versions.additional_compensation.bulk_create.modal.open_contract'
          ),
          icon: ExternalLink,
          type: 'primary' as const,
          onClick: () => {
            window.open(
              navigation.employees.id.contractVersions.fullPath({
                id: employee.id.toString(),
              }),
              '_blank'
            )
          },
        }

        if (ids.length === 1) {
          return [agreementAction]
        }

        return [
          agreementAction,
          {
            label: i18n.t('contract_versions.additional_compensation.bulk_create.modal.remove'),
            icon: Delete,
            type: 'primary' as const,
            onClick: () => {
              onRemoveEmployee(employee.id)
            },
          },
        ]
      },
    },
    [ids]
  )

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.t('contract_versions.additional_compensation.bulk_create.modal.review_employees')}
    >
      <Box padding="s24" gap="s24">
        <OneDataCollection
          source={source}
          visualizations={[
            {
              type: 'list',
              options: {
                itemDefinition: (item: ReviewEmployeesModalEmployeeFragment) => ({
                  title: item.fullName,
                  avatar: {
                    type: 'person',
                    src: buildUrl(item.avatar?.url) ?? '',
                    firstName: item.firstName,
                    lastName: item.lastName,
                  },
                }),
                fields: [
                  {
                    label: i18n.t('common.team'),
                    render: (item: ReviewEmployeesModalEmployeeFragment) => {
                      const teams = item.memberships
                        .map((membership) => membership.team?.name)
                        .join(', ')

                      return {
                        type: 'tag',
                        value: {
                          label:
                            teams ||
                            i18n.t(
                              'contract_versions.additional_compensation.bulk_create.modal.without_team'
                            ),
                        },
                      }
                    },
                  },
                ],
              },
            },
          ]}
        />
      </Box>
    </F0Dialog>
  )
}

export default ReviewEmployeesModal
